import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { clearCrmLeadPrefill, crmLinkBooking, readCrmLeadPrefill } from "../../../../api/crmApi";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Bookings from "../../../bookings/Bookings";
import WalkInDraftsPanel from "./WalkInDraftsPanel";
import { getAdminWalkInBookings } from "../../../../api/adminWalkInBookingApi";
import {
  pickWalkInTrackingToken,
  publicTrackPath,
  publicTrackUrl,
} from "../../../../utils/adminWalkInBooking";
import { formatCurrency } from "../../../../utils/formatCurrency";
import {
  deleteWalkInDraft,
  getActiveWalkInDraftId,
  listWalkInDrafts,
  refreshWalkInDraftsFromServer,
  setActiveWalkInDraftId,
} from "../../../../utils/walkInBookingDraft";

const formatDate = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const TrackUrlCell = ({ row }) => {
  const [copied, setCopied] = useState(false);
  const token = pickWalkInTrackingToken(row);
  const path = publicTrackPath(token);
  const url = publicTrackUrl(token);
  if (!path) return <span className="text-stone-400">—</span>;

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Link
        to={path}
        target="_blank"
        rel="noreferrer"
        className="text-sm font-semibold text-orange-700 hover:underline truncate max-w-[220px]"
        title={url}
      >
        {url}
      </Link>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
          } catch {
            /* ignore */
          }
        }}
        className="shrink-0 h-7 px-2 rounded-md border border-stone-200 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const AdminWalkInBookings = () => {
  const location = useLocation();
  const crmLead = location.state?.crmLeadQueryId
    ? { queryId: location.state.crmLeadQueryId, ...(readCrmLeadPrefill() || {}) }
    : readCrmLeadPrefill();
  const [tab, setTab] = useState("create");
  const [wizardKey, setWizardKey] = useState(0);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshDrafts = useCallback(async () => {
    try {
      const list = await refreshWalkInDraftsFromServer();
      setDrafts(list);
    } catch (err) {
      setDrafts(listWalkInDrafts());
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load drafts from the server.",
      );
    }
  }, []);

  const mergeDraft = useCallback((saved) => {
    if (!saved) return;
    setDrafts((prev) => [
      saved,
      ...prev.filter(
        (item) => item.id !== saved.id && item.clientKey !== saved.clientKey,
      ),
    ]);
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getAdminWalkInBookings();
      setRows(list);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load walk-in bookings.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    refreshDrafts();
  }, [fetchRows, refreshDrafts]);

  return (
    <SetupContainer>
      <Box top>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-600">
              Admin only
            </p>
            <h1 className="text-lg font-semibold tracking-tight text-stone-900">
              Walk-in booking
            </h1>
            <p className="text-xs text-stone-500 mt-0.5">
              Counter bookings with optional sender/receiver accounts. Save a
              draft and start another at any time.
            </p>
          </div>
          {tab === "history" ? (
            <button
              type="button"
              onClick={fetchRows}
              disabled={loading}
              className="h-8 px-3 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 disabled:opacity-60"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          ) : null}
        </div>

        <div className="inline-flex rounded-lg border border-stone-200 bg-stone-50 p-0.5 mb-3">
          <button
            type="button"
            onClick={() => setTab("create")}
            className={`h-8 px-3 rounded-md text-xs font-semibold ${
              tab === "create" ? "bg-orange-600 text-white" : "text-stone-600"
            }`}
          >
            New booking
          </button>
          <button
            type="button"
            onClick={() => {
              refreshDrafts();
              setTab("drafts");
            }}
            className={`h-8 px-3 rounded-md text-xs font-semibold ${
              tab === "drafts" ? "bg-orange-600 text-white" : "text-stone-600"
            }`}
          >
            Drafts{drafts.length ? ` (${drafts.length})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setTab("history")}
            className={`h-8 px-3 rounded-md text-xs font-semibold ${
              tab === "history" ? "bg-orange-600 text-white" : "text-stone-600"
            }`}
          >
            History
          </button>
        </div>

        {crmLead?.queryId && tab === "create" ? (
          <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
            Creating a walk-in booking from CRM lead #{crmLead.queryId}
            {crmLead.senderName ? ` — ${crmLead.senderName}` : ""}.
          </div>
        ) : null}
        {tab === "create" ? (
          <Bookings
            key={wizardKey}
            tittle="Walk-in Booking"
            flow="walkin"
            variant="admin"
            onWalkInDraftChange={mergeDraft}
            onWalkInStartAnother={() => {
              setActiveWalkInDraftId("");
              setWizardKey((k) => k + 1);
              setTab("create");
              refreshDrafts();
            }}
            onWalkInCreated={async (result) => {
              const queryId = crmLead?.queryId;
              if (queryId && result?.parcelId) {
                try {
                  await crmLinkBooking(queryId, result.parcelId, result.paymentInvoiceNo);
                  toast.success("Lead linked to this booking. It now appears in CRM Orders and Bookings.");
                  clearCrmLeadPrefill();
                } catch (err) {
                  toast.error(err?.response?.data || err?.message || "Could not link the CRM lead.");
                }
              }
              setActiveWalkInDraftId("");
              setWizardKey((k) => k + 1);
              setTab("create");
              refreshDrafts();
              fetchRows();
            }}
          />
        ) : tab === "drafts" ? (
          <WalkInDraftsPanel
            drafts={drafts}
            activeId={getActiveWalkInDraftId()}
            onContinue={(id) => {
              setActiveWalkInDraftId(id);
              setWizardKey((k) => k + 1);
              setTab("create");
            }}
            onDelete={async (id) => {
              setDrafts((prev) =>
                prev.filter((item) => item.id !== id && item.clientKey !== id),
              );
              try {
                await deleteWalkInDraft(id);
                await refreshDrafts();
                toast.success("Draft deleted.");
              } catch (err) {
                toast.error(
                  err?.response?.data?.message ||
                    err?.message ||
                    "Could not delete the draft.",
                );
                await refreshDrafts();
              }
            }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="bg-stone-100 text-[11px] font-semibold uppercase tracking-wide text-stone-500 text-left">
                  <th className="py-2 px-3">Serial</th>
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Sender</th>
                  <th className="py-2 px-3">Receiver</th>
                  <th className="py-2 px-3">Owner</th>
                  <th className="py-2 px-3">Amount</th>
                  <th className="py-2 px-3">Tracking URL</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-sm text-stone-500">
                      Loading walk-in bookings…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-sm text-stone-500">
                      No walk-in bookings yet.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={row.id ?? row.recordSerialNo ?? idx}
                      className={idx % 2 ? "bg-stone-50" : "bg-white"}
                    >
                      <td className="py-2.5 px-3 text-sm font-semibold">
                        {row.recordSerialNo || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-sm whitespace-nowrap">
                        {formatDate(row.creationDate)}
                      </td>
                      <td className="py-2.5 px-3 text-sm">{row.senderName || "—"}</td>
                      <td className="py-2.5 px-3 text-sm">{row.receiverName || "—"}</td>
                      <td className="py-2.5 px-3 text-sm">{row.bookingOwner || "—"}</td>
                      <td className="py-2.5 px-3 text-sm font-semibold">
                        {formatCurrency(row.amount, row.currency || "GBP")}
                      </td>
                      <td className="py-2.5 px-3 text-sm">
                        <TrackUrlCell row={row} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    </SetupContainer>
  );
};

export default AdminWalkInBookings;
